import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

/**
 * One file attached to a CURA conversation.
 *
 * `url` and `pageImageUrls` are SHORT-LIVED SAS links minted per response by the server —
 * they are not stable identifiers and must not be cached or persisted. Re-list to refresh.
 */
export interface CuraAttachment {
  id: string;
  patientId: string;
  conversationKey: string;
  mediaType: string;
  fileName: string;
  url: string;
  pageImageUrls: string[] | null;
  pageCount: number | null;
  promoted: boolean;
  expiresAt: string;
  createdAt: string;
}

/**
 * Client for CURA chat attachments — tasks/cura-form-filling.md Phase 1.
 *
 * The limits below MIRROR the server's (controllers/curaAttachmentController.js). They exist to
 * give immediate feedback, not to enforce anything: the server re-checks every one of them and
 * its answer is the one that counts.
 */
@Injectable({ providedIn: 'root' })
export class CuraAttachmentService {
  private apiUrl = 'api';

  /** Kept in step with ALLOWED_MIME server-side. */
  static readonly ACCEPT = 'application/pdf,image/*,.docx';
  static readonly MAX_FILES_PER_REQUEST = 5;
  static readonly MAX_BYTES_PER_FILE = 25 * 1024 * 1024;
  static readonly MAX_ACTIVE_PER_CONVERSATION = 10;

  constructor(private http: HttpClient) { }

  upload(patientId: string, conversationKey: string, files: File[]): Observable<CuraAttachment[]> {
    const form = new FormData();
    form.append('patientId', patientId);
    form.append('conversationKey', conversationKey);
    files.forEach(f => form.append('files', f, f.name));
    return this.http
      .post<{ success: boolean; data: CuraAttachment[] }>(`${this.apiUrl}/cura-attachments`, form)
      .pipe(map(r => r.data));
  }

  list(patientId: string, conversationKey: string): Observable<CuraAttachment[]> {
    return this.http
      .get<{ success: boolean; data: CuraAttachment[] }>(
        `${this.apiUrl}/cura-attachments?patientId=${patientId}&conversationKey=${conversationKey}`)
      .pipe(map(r => r.data));
  }

  remove(id: string): Observable<void> {
    return this.http
      .delete<{ success: boolean }>(`${this.apiUrl}/cura-attachments/${id}`)
      .pipe(map(() => void 0));
  }

  /**
   * Local pre-flight. Returns an error string, or null when the batch looks acceptable.
   *
   * Deliberately does NOT try to detect a legacy .doc by extension — that is the server's job,
   * done from the file's actual bytes, because an extension can be renamed. This only catches
   * the obvious cases early so the user is not made to wait for an upload that cannot succeed.
   */
  validate(files: File[], alreadyAttached: number): string | null {
    if (!files.length) return null;
    if (files.length > CuraAttachmentService.MAX_FILES_PER_REQUEST) {
      return `Up to ${CuraAttachmentService.MAX_FILES_PER_REQUEST} files at a time.`;
    }
    if (alreadyAttached + files.length > CuraAttachmentService.MAX_ACTIVE_PER_CONVERSATION) {
      return `A conversation can hold ${CuraAttachmentService.MAX_ACTIVE_PER_CONVERSATION} attachments. Remove one first.`;
    }
    const tooBig = files.find(f => f.size > CuraAttachmentService.MAX_BYTES_PER_FILE);
    if (tooBig) {
      const mb = CuraAttachmentService.MAX_BYTES_PER_FILE / (1024 * 1024);
      return `${tooBig.name} is larger than ${mb}MB.`;
    }
    const legacyDoc = files.find(f => /\.doc$/i.test(f.name));
    if (legacyDoc) {
      return `${legacyDoc.name} is an old-format Word document. Open it in Word, ` +
        `choose File → Save As and pick .docx, then upload that.`;
    }
    return null;
  }

  /** Rough icon choice for a chip, from the mime type. */
  iconFor(mediaType: string): string {
    if (mediaType === 'application/pdf') return 'picture_as_pdf';
    if (mediaType.startsWith('image/')) return 'image';
    if (mediaType.includes('wordprocessingml')) return 'description';
    return 'insert_drive_file';
  }
}
